import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  InputAdornment,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../api';

// Read the HR auth token from where the rest of the app stores it (authData),
// mirroring the axios interceptor in src/api/index.js. Previously this component
// read a non-existent 'token' key, sending "Bearer null" on every request.
const getAuthToken = () => {
  try {
    const data = JSON.parse(localStorage.getItem('authData'));
    return data?.admin?.Token || data?.token || null;
  } catch {
    return null;
  }
};

function WhatsAppDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch contacts/conversations from Django API
  useEffect(() => {
    fetchContacts();
  }, []);

  // Fetch messages when contact is selected
  useEffect(() => {
    if (selectedContact) {
      fetchMessages(selectedContact.phone_number);
    }
  }, [selectedContact]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl('api/whatsapp/contacts/'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch contacts');

      const data = await response.json();
      setContacts(data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (phoneNumber) => {
    setLoading(true);
    try {
      const response = await fetch(
        apiUrl(`api/whatsapp/messages/${phoneNumber}/`),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch messages');

      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedContact) return;

    setSending(true);
    const tempMessage = {
      id: Date.now(),
      message: messageInput,
      timestamp: new Date().toISOString(),
      is_from_me: true,
      status: 'sending',
    };

    setMessages((prev) => [...prev, tempMessage]);
    setMessageInput('');

    try {
      const response = await fetch(apiUrl('api/whatsapp/send/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          phone_number: selectedContact.phone_number,
          message: messageInput,
        }),
      });

      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();

      // Update message with server response
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempMessage.id
            ? { ...msg, id: data.message_id, status: 'sent' }
            : msg
        )
      );
    } catch (error) {
      console.error('Error sending message:', error);
      // Update message status to failed
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempMessage.id ? { ...msg, status: 'failed' } : msg
        )
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const filteredContacts = contacts.filter((contact) =>
    contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.phone_number?.includes(searchQuery)
  );

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckIcon sx={{ fontSize: 16, color: '#999' }} />;
      case 'delivered':
        return <DoneAllIcon sx={{ fontSize: 16, color: '#999' }} />;
      case 'read':
        return <DoneAllIcon sx={{ fontSize: 16, color: '#4fc3f7' }} />;
      default:
        return <CheckIcon sx={{ fontSize: 16, color: '#999' }} />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
        padding: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Container maxWidth="xl" sx={{ padding: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            marginBottom: 3,
            animation: 'fadeIn 0.5s ease-in-out',
          }}
        >
          <IconButton onClick={() => navigate(-1)} sx={{ color: '#25D366' }}>
            <ArrowBackIcon />
          </IconButton>
          <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366' }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            WhatsApp Business
          </Typography>
        </Box>

        {/* Main Chat Container */}
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            height: { xs: 'calc(100vh - 120px)', md: '75vh' },
            borderRadius: '16px',
            overflow: 'hidden',
            animation: 'fadeInUp 0.6s ease-in-out',
          }}
        >
          {/* Contacts Sidebar */}
          <Box
            sx={{
              width: { xs: selectedContact ? '0' : '100%', md: '35%' },
              display: { xs: selectedContact ? 'none' : 'flex', md: 'flex' },
              flexDirection: 'column',
              borderRight: '1px solid #e0e0e0',
              background: '#fff',
            }}
          >
            {/* Search Bar */}
            <Box sx={{ padding: 2, borderBottom: '1px solid #e0e0e0' }}>
              <TextField
                fullWidth
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                    backgroundColor: '#f0f2f5',
                    '& fieldset': { border: 'none' },
                  },
                }}
              />
            </Box>

            {/* Contacts List */}
            <List sx={{ flexGrow: 1, overflow: 'auto', padding: 0 }}>
              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <CircularProgress sx={{ color: '#25D366' }} />
                </Box>
              ) : (
                filteredContacts.map((contact) => (
                  <ListItem
                    key={contact.id}
                    button
                    selected={selectedContact?.id === contact.id}
                    onClick={() => setSelectedContact(contact)}
                    sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      '&.Mui-selected': {
                        backgroundColor: '#e8f5e9',
                        '&:hover': { backgroundColor: '#e8f5e9' },
                      },
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        color="success"
                        variant="dot"
                        invisible={!contact.is_online}
                        overlap="circular"
                      >
                        <Avatar sx={{ bgcolor: '#25D366' }}>
                          {contact.name?.charAt(0) || contact.phone_number?.charAt(0)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={contact.name || contact.phone_number}
                      secondary={
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#666',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '70%',
                            }}
                          >
                            {contact.last_message || 'No messages yet'}
                          </Typography>
                          {contact.unread_count > 0 && (
                            <Chip
                              label={contact.unread_count}
                              size="small"
                              sx={{
                                backgroundColor: '#25D366',
                                color: '#fff',
                                height: 20,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Box>

          {/* Chat Area */}
          <Box
            sx={{
              flexGrow: 1,
              display: selectedContact ? 'flex' : { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              background: '#e5ddd5',
            }}
          >
            {selectedContact ? (
              <>
                {/* Chat Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 2,
                    background: '#f0f2f5',
                    borderBottom: '1px solid #d1d7db',
                  }}
                >
                  {isMobile && (
                    <IconButton onClick={() => setSelectedContact(null)} sx={{ mr: 1 }}>
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  <Avatar sx={{ bgcolor: '#25D366', mr: 2 }}>
                    {selectedContact.name?.charAt(0) || selectedContact.phone_number?.charAt(0)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {selectedContact.name || selectedContact.phone_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedContact.is_online ? 'Online' : 'Offline'}
                    </Typography>
                  </Box>
                  <IconButton>
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                {/* Messages Container */}
                <Box
                  sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    padding: 2,
                    backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
                    backgroundSize: 'cover',
                  }}
                >
                  {messages.map((message, index) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: message.is_from_me ? 'flex-end' : 'flex-start',
                        marginBottom: 1,
                        animation: 'fadeIn 0.3s ease-in-out',
                      }}
                    >
                      <Paper
                        sx={{
                          padding: '8px 12px',
                          maxWidth: '70%',
                          borderRadius: message.is_from_me
                            ? '10px 10px 0 10px'
                            : '10px 10px 10px 0',
                          backgroundColor: message.is_from_me ? '#dcf8c6' : '#fff',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {message.message}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 0.5,
                            marginTop: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#667781', fontSize: '0.7rem' }}
                          >
                            {formatTime(message.timestamp)}
                          </Typography>
                          {message.is_from_me && getStatusIcon(message.status)}
                        </Box>
                      </Paper>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 1.5,
                    background: '#f0f2f5',
                    gap: 1,
                  }}
                >
                  <IconButton size="small">
                    <EmojiEmotionsIcon />
                  </IconButton>
                  <IconButton size="small">
                    <AttachFileIcon />
                  </IconButton>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Type a message"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '20px',
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                  <IconButton
                    onClick={sendMessage}
                    disabled={!messageInput.trim() || sending}
                    sx={{
                      backgroundColor: '#25D366',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#128C7E' },
                      '&.Mui-disabled': { backgroundColor: '#ccc' },
                    }}
                  >
                    {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                  </IconButton>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#667781',
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 120, color: '#25D366', marginBottom: 2 }} />
                <Typography variant="h5" fontWeight={600}>
                  WhatsApp Business
                </Typography>
                <Typography variant="body2" sx={{ marginTop: 1 }}>
                  Select a contact to start messaging
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes fadeInUp {
            from { 
              opacity: 0; 
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Box>
  );
}

export default WhatsAppDashboard;